import {
  Box,
  Flex,
  IntrinsicImage,
  Link,
  SanityRichText,
  Section,
  SxStyleProp,
  Text,
} from '@tishman/components';
import { Block } from '@sanity/block-content-to-react';
import React from 'react';

import { PrivateEventsHeroVenueDetails } from '../PrivateEventsHeroVenueDetails';
import { TVenueDetail } from '../PrivateEventsHero';
import { getPrivateEventsPageNameForHeroCta } from '../../../utils/privateEventsPageName';
type TPrivateEventsHeroTwoColumn = {
  eyebrow?: string;
  title?: string;
  rawHeroLongText?: Block[] | string | null;
  cta?: {
    url: string | null;
    caption: string | null;
  };
  image?: Queries.SanityImageType;
  venueDetails?: TVenueDetail[];
  openInNewWindow?: boolean;
  venueDetailsSx?: SxStyleProp;
  type?: 'default' | 'venue-category' | 'venue-detail';
  pageName?: string;
};
export const PrivateEventsHeroTwoColumn = ({
  eyebrow,
  title,
  rawHeroLongText,
  cta,
  image,
  venueDetails,
  openInNewWindow = true,
  type = 'venue-detail',
  venueDetailsSx,
  pageName = '',
}: TPrivateEventsHeroTwoColumn) => {
  return (
    <Section
      id="details"
      sx={{ marginTop: ['24px', 0] }}
      theme="Rock Center White"
    >
      <Flex
        sx={{
          flexDirection: ['column-reverse', 'column-reverse', 'row'],
          gap: ['32px', 0],
          paddingBottom: ['32px', 0],
        }}
      >
        <Flex
          sx={{
            padding: ['4px 16px', '80px 67px 80px 72px'],
            alignItems: 'flex-start',
            alignContent: 'center',
            gap: '48px',
            flex: ['1 0 100%', '1 0 50%'],
            flexWrap: 'wrap',
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '32px',
            }}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
                width: '100%',
                flex: '0 0 auto',
                gap: '16px',
                marginBottom: ['16px', 0],
                maxWidth: '530px',
              }}
            >
              <Text sx={{ color: 'pageName' }} variant="bodyEmphasis">
                {eyebrow}
              </Text>
              <Text
                as="h1"
                sx={{ fontSize: ['32px', '32px', '32px', '60px'] }}
                variant="h1"
              >
                {title}
              </Text>
              {typeof rawHeroLongText === 'string' && (
                <Text sx={{ fontSize: ['16px', '18px'] }} variant="body">
                  {rawHeroLongText}
                </Text>
              )}
              {typeof rawHeroLongText === 'object' && rawHeroLongText !== null && (
                <SanityRichText
                  blocks={rawHeroLongText}
                  components={{
                    p: ({ children }) => (
                      <Text sx={{ fontSize: ['16px', '18px'] }} variant="body">
                        {children as React.ReactNode}
                      </Text>
                    ),
                  }}
                />
              )}
            </Flex>
            {cta && (
              <Link
                data-gtm-id={getPrivateEventsPageNameForHeroCta(pageName)}
                sx={{ width: ['100%', 'auto'], textAlign: 'center' }}
                to={cta.url || ''}
                variant="button"
              >
                {cta.caption}
              </Link>
            )}
          </Flex>
          {Array.isArray(venueDetails) && venueDetails.length > 0 && (
            <Box
              sx={{
                ...venueDetailsSx,
                display: 'block',
                marginBottom: [type === 'venue-detail' ? '40px' : 0, 0],
              }}
            >
              {venueDetails?.map((venueDetail, index) => (
                <PrivateEventsHeroVenueDetails
                  blocks={venueDetail.content}
                  key={index}
                  last={index === venueDetails.length - 1}
                  links={venueDetail.links}
                  openInNewWindow={openInNewWindow}
                  type={type}
                />
              ))}
            </Box>
          )}
        </Flex>
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            flex: ['1 0 100%', '1 0 50%'],
            width: '100%',
          }}
        >
          {image && image.asset?.gatsbyImageData && (
            <IntrinsicImage
              alt={image.alt || image.caption || ''}
              image={image.asset?.gatsbyImageData}
              sx={{
                width: '100%',
                height: ['278px', '278px', '100%'],
              }}
            />
          )}
        </Flex>
      </Flex>
    </Section>
  );
};
